import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, SystemSettings, GlobalPricing, PlanType } from '../types';
import { api } from '../services/api';
import { ProfileForm } from './ProfileForm';
import { Database, Shield, Globe, Mail, Lock, CheckCircle, AlertTriangle, Loader2, DollarSign, MessageSquare, Settings, Users, Bot } from 'lucide-react';
import { AgencyTeamView } from './AgencyTeamView';

// Data Lists for Regional Settings
const TIMEZONES = [
    { label: 'UTC (Universal Coordinated Time)', value: 'UTC' },
    { label: 'UTC-12:00 (International Date Line West)', value: 'Etc/GMT+12' },
    { label: 'UTC-11:00 (Midway Island, Samoa)', value: 'Pacific/Midway' },
    { label: 'UTC-10:00 (Hawaii)', value: 'Pacific/Honolulu' },
    { label: 'UTC-09:00 (Alaska)', value: 'America/Anchorage' },
    { label: 'UTC-08:00 (Pacific Time - US & Canada)', value: 'America/Los_Angeles' },
    { label: 'UTC-07:00 (Mountain Time - US & Canada)', value: 'America/Denver' },
    { label: 'UTC-06:00 (Central Time - US & Canada)', value: 'America/Chicago' },
    { label: 'UTC-05:00 (Eastern Time - US & Canada)', value: 'America/New_York' },
    { label: 'UTC-04:00 (Atlantic Time - Canada)', value: 'America/Halifax' },
    { label: 'UTC-03:00 (Buenos Aires)', value: 'America/Argentina/Buenos_Aires' },
    { label: 'UTC-02:00 (Mid-Atlantic)', value: 'Atlantic/South_Georgia' },
    { label: 'UTC-01:00 (Azores)', value: 'Atlantic/Azores' },
    { label: 'UTC+00:00 (London, Edinburgh, Dublin)', value: 'Europe/London' },
    { label: 'UTC+01:00 (Central European Time)', value: 'Europe/Paris' },
    { label: 'UTC+02:00 (Eastern European Time)', value: 'Europe/Athens' },
    { label: 'UTC+03:00 (Moscow, Nairobi)', value: 'Europe/Moscow' },
    { label: 'UTC+03:30 (Tehran)', value: 'Asia/Tehran' },
    { label: 'UTC+04:00 (Dubai, Baku)', value: 'Asia/Dubai' },
    { label: 'UTC+04:30 (Kabul)', value: 'Asia/Kabul' },
    { label: 'UTC+05:00 (Karachi, Tashkent)', value: 'Asia/Karachi' },
    { label: 'UTC+05:30 (India Standard Time)', value: 'Asia/Kolkata' },
    { label: 'UTC+05:45 (Kathmandu)', value: 'Asia/Kathmandu' },
    { label: 'UTC+06:00 (Dhaka)', value: 'Asia/Dhaka' },
    { label: 'UTC+06:30 (Yangon)', value: 'Asia/Yangon' },
    { label: 'UTC+07:00 (Bangkok, Hanoi, Jakarta)', value: 'Asia/Bangkok' },
    { label: 'UTC+08:00 (Beijing, Hong Kong, Singapore)', value: 'Asia/Shanghai' },
    { label: 'UTC+09:00 (Tokyo, Seoul)', value: 'Asia/Tokyo' },
    { label: 'UTC+09:30 (Adelaide)', value: 'Australia/Adelaide' },
    { label: 'UTC+10:00 (Sydney, Melbourne)', value: 'Australia/Sydney' },
    { label: 'UTC+11:00 (Solomon Is., New Caledonia)', value: 'Pacific/Guadalcanal' },
    { label: 'UTC+12:00 (Fiji, Kamchatka)', value: 'Pacific/Fiji' },
    { label: 'UTC+13:00 (Nuku\'alofa)', value: 'Pacific/Tongatapu' },
];

const CURRENCIES = [
    { label: 'USD ($)', value: 'USD' },
    { label: 'EUR (€)', value: 'EUR' },
    { label: 'GBP (£)', value: 'GBP' },
    { label: 'JPY (¥)', value: 'JPY' },
    { label: 'AUD (A$)', value: 'AUD' },
    { label: 'CAD (C$)', value: 'CAD' },
    { label: 'CHF (Fr)', value: 'CHF' },
    { label: 'CNY (¥)', value: 'CNY' },
    { label: 'INR (₹)', value: 'INR' },
];

const DATE_FORMATS = [
    { label: 'DD/MM/YYYY (e.g. 31/12/2024)', value: 'DD/MM/YYYY' },
    { label: 'MM/DD/YYYY (e.g. 12/31/2024)', value: 'MM/DD/YYYY' },
    { label: 'YYYY-MM-DD (e.g. 2024-12-31)', value: 'YYYY-MM-DD' },
];

const LANGUAGES = [
    { label: 'English', value: 'en' },
    { label: 'Ελληνικά', value: 'el' },
];

export const SystemSettingsView = ({ user, onUserUpdate }: { user: User, onUserUpdate: () => void }) => {
    const { t } = useTranslation('settings');
    // Default to 'account' if agency_member, as they cannot see profile
    const [tab, setTab] = useState<'profile' | 'account' | 'system'>(user.role === 'agency_member' ? 'account' : 'profile');
    const [settings, setSettings] = useState<SystemSettings>({
        aiApiKey: '',
        emailJsServiceId: '',
        emailJsTemplateId: '',
        emailJsPublicKey: ''
    });
    const [pricing, setPricing] = useState<GlobalPricing | null>(null);

    // Admin: Load Global Settings
    // Admin: Load Global Settings
    useEffect(() => {
        if (user.role === 'admin') {
            api.getSystemSettings().then(setSettings);
            api.getGlobalPricing().then((data: any) => {
                // Ensure all plans exist
                const defaultPricing: GlobalPricing = {
                    [PlanType.LIGHT]: { monthly: 19, yearly: 190 },
                    [PlanType.ADVANCED]: { monthly: 49, yearly: 490 },
                    [PlanType.AGENCY]: { monthly: 199, yearly: 1990 }, // DefaultAgency
                };
                setPricing(data ? { ...defaultPricing, ...data } : defaultPricing);
            });
        }
    }, [user.role]);

    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await api.updateSystemSettings(settings);
            if (pricing) {
                await api.updateGlobalPricing(pricing);
            }
            alert('System settings saved successfully.');
        } catch (error: any) {
            console.error("Save Error:", error);
            alert(`Failed to save settings: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (key: keyof SystemSettings, value: string) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const handlePriceChange = (plan: PlanType, field: 'monthly' | 'yearly', value: string) => {
        if (pricing) {
            setPricing({
                ...pricing,
                [plan]: { ...pricing[plan], [field]: parseFloat(value) || 0 }
            });
        }
    };

    // Password / Email State
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [newEmail, setNewEmail] = useState(user.email || '');
    const [isUpdating, setIsUpdating] = useState(false);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Regional State
    const [prefTimezone, setPrefTimezone] = useState(user.timezone || 'UTC');
    const [prefCurrency, setPrefCurrency] = useState(user.currency || 'USD');
    const [prefDateFormat, setPrefDateFormat] = useState(user.dateFormat || 'DD/MM/YYYY');
    const [prefLanguage, setPrefLanguage] = useState(user.language || 'en');
    const [isSavingPrefs, setIsSavingPrefs] = useState(false);

    // General Preferences
    const [enableDocNotes, setEnableDocNotes] = useState(user.config?.enableDocumentNotes !== false);
    const [enableAi, setEnableAi] = useState(user.config?.ai_features?.document_ai_enabled !== false); // Default to true if undefined, or false? Let's default true for "opt-out" or false for "opt-in"? 
    // Actually, user requested "Enable or disable", implying it might be active. Let's match current state.
    // If undefined, it acts as enabled or disabled? In `BulkUploadWizard` and `App.tsx` table, we check `if (client.config?.ai_features?.document_ai_enabled)`. undefined is falsy.
    // So default should be strict match.
    // user.config?.ai_features?.document_ai_enabled === true.

    // Correction: In the previous step for Bulk Upload, I checked `if (client.config?.ai_features?.document_ai_enabled)`.
    // So if it's undefined, it's disabled.
    // BUT the prompt implies they want to ENABLE it.
    // Let's initialize state based on truthiness.
    // Note: React state needs to handle the toggle change.

    const [enableAiGlobal, setEnableAiGlobal] = useState(!!user.config?.ai_features?.document_ai_enabled);

    const [isSavingGeneral, setIsSavingGeneral] = useState(false);

    const handleSaveGeneral = async () => {
        setIsSavingGeneral(true);
        try {
            await api.updateUserConfig(user.id, {
                enableDocumentNotes: enableDocNotes,
                ai_features: {
                    ...user.config?.ai_features,
                    document_ai_enabled: enableAiGlobal
                }
            });
            setMsg({ type: 'success', text: 'General preferences saved successfully.' });
            onUserUpdate();
        } catch (e: any) {
            setMsg({ type: 'error', text: 'Failed to save general preferences: ' + e.message });
        } finally {
            setIsSavingGeneral(false);
        }
    };

    const updatePass = async () => {
        setMsg(null);
        if (!newPassword) return;
        if (newPassword.length < 6) {
            setMsg({ type: 'error', text: t('account.messages.pass_length') });
            return;
        }
        if (newPassword !== confirmPassword) {
            setMsg({ type: 'error', text: t('account.messages.pass_mismatch') });
            return;
        }
        setIsUpdating(true);
        try {
            await api.updateUserPassword(newPassword);
            setMsg({ type: 'success', text: t('account.messages.password_updated') });
            setNewPassword('');
            setConfirmPassword('');
        } catch (e: any) {
            setMsg({ type: 'error', text: e.message });
        } finally {
            setIsUpdating(false);
        }
    };

    const updateEmail = async () => {
        if (newEmail && newEmail !== user.email) {
            setIsUpdating(true);
            try {
                await api.updateUserEmail(newEmail);
                setMsg({ type: 'success', text: t('account.messages.email_updated') });
                onUserUpdate();
            } catch (e: any) {
                setMsg({ type: 'error', text: e.message });
            } finally {
                setIsUpdating(false);
            }
        }
    };

    const handleSavePreferences = async () => {
        setIsSavingPrefs(true);
        try {
            await api.updateUserPreferences(user.id, {
                timezone: prefTimezone,
                currency: prefCurrency,
                dateFormat: prefDateFormat,
                language: prefLanguage
            });
            setMsg({ type: 'success', text: t('account.messages.preferences_saved') });
            onUserUpdate();
        } catch (e: any) {
            setMsg({ type: 'error', text: 'Failed to save preferences: ' + e.message });
        } finally {
            setIsSavingPrefs(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-2 overflow-x-auto pb-2">
                {user.role !== 'agency_member' && <button onClick={() => setTab('profile')} className={`px-5 py-2.5 font-bold text-sm rounded-xl transition-all ${tab === 'profile' ? 'bg-black text-white shadow-lg' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>{t('tabs.profile')}</button>}
                <button onClick={() => setTab('account')} className={`px-5 py-2.5 font-bold text-sm rounded-xl transition-all ${tab === 'account' ? 'bg-black text-white shadow-lg' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>{t('tabs.account')}</button>
                {user.role === 'admin' && (
                    <button onClick={() => setTab('system')} className={`px-5 py-2.5 font-bold text-sm rounded-xl transition-all ${tab === 'system' ? 'bg-black text-white shadow-lg' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>{t('tabs.system')}</button>
                )}
                {user.role === 'agency' && (
                    <button onClick={() => setTab('team' as any)} className={`px-5 py-2.5 font-bold text-sm rounded-xl transition-all ${tab === 'team' as any ? 'bg-black text-white shadow-lg' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>{t('tabs.team')}</button>
                )}
            </div>

            {msg && (
                <div className={`p-4 rounded-xl text-sm font-medium flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {msg.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
                    {msg.text}
                </div>
            )}

            {tab === 'profile' && user.role !== 'agency_member' && (
                <ProfileForm user={user} onSave={onUserUpdate} />
            )}

            {
                tab === 'account' && (
                    <div className="space-y-6">
                        {/* General Preferences */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2"><Settings size={20} /> General Preferences</h2>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-bold text-slate-900 flex items-center gap-2">
                                            <MessageSquare size={16} className="text-slate-500" />
                                            Document Notes & Discussion
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">
                                            Enable or disable the query/notes functionality on documents.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => (user.role === 'agency' || user.role === 'admin') && setEnableDocNotes(!enableDocNotes)}
                                        disabled={user.role !== 'agency' && user.role !== 'admin'}
                                        className={`w-12 h-6 rounded-full transition-colors relative ${enableDocNotes ? 'bg-black' : 'bg-slate-200'} ${user.role !== 'agency' && user.role !== 'admin' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${enableDocNotes ? 'left-7' : 'left-1'}`}></span>
                                    </button>
                                </div>

                                {/* Global AI Advisor Toggle */}
                                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                                    <div>
                                        <div className="font-bold text-slate-900 flex items-center gap-2">
                                            <Bot size={16} className="text-purple-600" />
                                            AI Advisor (Global)
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">
                                            Enable or disable AI features for your agency account.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setEnableAiGlobal(!enableAiGlobal)}
                                        className={`w-12 h-6 rounded-full transition-colors relative ${enableAiGlobal ? 'bg-purple-600' : 'bg-slate-200'}`}
                                    >
                                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${enableAiGlobal ? 'left-7' : 'left-1'}`}></span>
                                    </button>
                                </div>

                            </div>
                            <div className="flex justify-end mt-6">
                                <button onClick={handleSaveGeneral} disabled={isSavingGeneral} className="bg-black text-white px-6 py-2 rounded-xl font-bold hover:opacity-80 transition flex items-center gap-2">
                                    {isSavingGeneral && <Loader2 className="animate-spin" size={16} />}
                                    Save Changes
                                </button>
                            </div>
                        </div>

                        {/* Regional Preferences */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2"><Globe size={20} /> {t('account.regional_title')}</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('account.timezone')}</label>
                                    <select value={prefTimezone} onChange={(e) => setPrefTimezone(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none bg-white focus:ring-2 focus:ring-black">
                                        {TIMEZONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('account.currency')}</label>
                                    <select value={prefCurrency} onChange={(e) => setPrefCurrency(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none bg-white focus:ring-2 focus:ring-black">
                                        {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('account.language')}</label>
                                    <select value={prefLanguage} onChange={(e) => setPrefLanguage(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none bg-white focus:ring-2 focus:ring-black">
                                        {LANGUAGES.map(l => <option key={l.value} value={l.value}>{t(`account.languages.${l.value}`)}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('account.date_format')}</label>
                                    <select value={prefDateFormat} onChange={(e) => setPrefDateFormat(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none bg-white focus:ring-2 focus:ring-black">
                                        {DATE_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="flex justify-end mt-6">
                                <button onClick={handleSavePreferences} disabled={isSavingPrefs} className="bg-black text-white px-6 py-2 rounded-xl font-bold hover:opacity-80 transition flex items-center gap-2">
                                    {isSavingPrefs && <Loader2 className="animate-spin" size={16} />}
                                    {t('account.save_preferences')}
                                </button>
                            </div>
                        </div>

                        {/* Account Security */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2"><Shield size={20} /> {t('account.security_title')}</h2>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">{t('account.email')}</label>
                                    <div className="flex gap-3">
                                        <div className="relative flex-1">
                                            <Mail className="absolute left-3 top-3 text-slate-400" size={18} />
                                            <input
                                                type="email"
                                                value={newEmail}
                                                onChange={(e) => setNewEmail(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-black"
                                            />
                                        </div>
                                        <button onClick={updateEmail} disabled={isUpdating || newEmail === user.email} className="bg-slate-100 px-6 py-2.5 rounded-xl font-bold text-slate-700 hover:bg-slate-200 transition disabled:opacity-50">{t('account.update')}</button>
                                    </div>
                                </div>
                                <div className="border-t border-slate-100 pt-6">
                                    <h3 className="text-sm font-bold text-slate-900 mb-4">{t('account.change_password')}</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('account.new_password')}</label>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-3 text-slate-400" size={18} />
                                                <input
                                                    type="password"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-black"
                                                    placeholder="••••••••"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('account.confirm_password')}</label>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-3 text-slate-400" size={18} />
                                                <input
                                                    type="password"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-black"
                                                    placeholder="••••••••"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={updatePass}
                                        disabled={isUpdating || !newPassword}
                                        className="mt-4 bg-black text-white px-6 py-2.5 rounded-xl font-bold hover:opacity-80 transition flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {isUpdating && <Loader2 className="animate-spin" size={16} />}
                                        {t('account.update_password')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                tab === 'system' && user.role === 'admin' && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                        <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2"><Database size={20} /> System Configuration</h2>
                        {/* ... (existing system config content) ... */}
                        {/* Simplified for brevity in replacement, but wait, I can't simplify invalidly. */}
                        {/* I should target the end of the file or just append the new tab check before the closing div. */}
                        {/* Retrying target to be precise. */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Google Gemini API Key</label>
                                <input type="password" value={settings.aiApiKey} onChange={(e) => handleChange('aiApiKey', e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:ring-2 focus:ring-black" placeholder="Enter Gemini API Key" />
                            </div>

                            <div className="pt-4 border-t border-slate-100">
                                <h3 className="text-sm font-bold text-slate-800 mb-4 uppercase tracking-wider">EmailJS Configuration</h3>
                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 mb-1">Service ID</label>
                                        <input type="text" value={settings.emailJsServiceId} onChange={(e) => handleChange('emailJsServiceId', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black" placeholder="service_xxx" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 mb-1">Template ID</label>
                                        <input type="text" value={settings.emailJsTemplateId} onChange={(e) => handleChange('emailJsTemplateId', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black" placeholder="template_xxx" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 mb-1">Public Key</label>
                                        <input type="text" value={settings.emailJsPublicKey} onChange={(e) => handleChange('emailJsPublicKey', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black" placeholder="Enter Public Key" />
                                    </div>
                                </div>
                            </div>

                            {pricing && (
                                <div className="pt-4 border-t border-slate-100">
                                    <h3 className="text-sm font-bold text-slate-800 mb-4 uppercase tracking-wider flex items-center gap-2">
                                        <DollarSign size={16} /> Pricing Configuration
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {[PlanType.LIGHT, PlanType.ADVANCED, PlanType.AGENCY].map((plan) => (
                                            <div key={plan} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                                <h4 className="font-bold text-sm text-slate-800 mb-2 capitalize">{plan} Plan</h4>
                                                <div className="space-y-2">
                                                    <div>
                                                        <label className="text-xs font-bold text-slate-500">Monthly ($)</label>
                                                        <input
                                                            type="number"
                                                            value={pricing[plan].monthly}
                                                            onChange={(e) => handlePriceChange(plan, 'monthly', e.target.value)}
                                                            className="w-full border border-slate-300 rounded p-1.5 text-sm outline-none focus:ring-1 focus:ring-black"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-bold text-slate-500">Yearly ($)</label>
                                                        <input
                                                            type="number"
                                                            value={pricing[plan].yearly}
                                                            onChange={(e) => handlePriceChange(plan, 'yearly', e.target.value)}
                                                            className="w-full border border-slate-300 rounded p-1.5 text-sm outline-none focus:ring-1 focus:ring-black"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <button onClick={handleSave} disabled={isSaving} className="mt-6 bg-black text-white px-6 py-2 rounded-xl font-bold hover:opacity-80 transition flex items-center gap-2">
                            {isSaving && <Loader2 className="animate-spin" size={16} />}
                            Save Settings
                        </button>
                    </div>
                )
            }

            {
                tab === 'team' as any && user.role === 'agency' && (
                    <AgencyTeamView user={user} />
                )
            }
        </div >
    );
};
